import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { riderApi } from '../api/riders';
import { isApiError, refusalMessage } from '../api/errors';
import { compressImageForUpload } from '../lib/compressImageToDataUrl';
import { clearDriverSession, isDriverSignedIn } from '../lib/driverSession';
import { useFleetRole } from '../lib/fleetRole';
import { CheckIcon, UplIcon } from '../components/driver/driverDocumentIcons';
import LicenceExpiryField from '../components/driver/LicenceExpiryField';
import './driverRegisterPremium.css';

/** A document's state, in the rider's words. */
export const STATE_WORDS = {
  pending: 'Waiting for the office',
  verified: 'Verified',
  rejected: 'Rejected. Send it again',
  expired: 'Expired. Send a new one',
};

/**
 * Where a rider's application stands (InGo ADR 0006 §3, ADR 0014; the
 * mobile plan's M2): signed in, not yet working. The server's sentence says
 * why; each document says what the office made of it, and one missing,
 * rejected or expired is sent again from here. Approved, the rider starts
 * working. The registration's own document buttons, so the two screens are
 * one.
 */
export default function DriverApplicationPage({ rider = riderApi, compress = compressImageForUpload, fleet }) {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [load, setLoad] = useState({ status: 'loading' });
  const [sending, setSending] = useState(null);
  const fleetRole = useFleetRole(fleet);
  const [problem, setProblem] = useState(() =>
    [
      state?.unsent?.length ? 'Some documents did not arrive. Send them again below.' : '',
      // A delivery company named at registration that could not be opened (InGo ADR 0032).
      state?.companyProblem ? `Your company was not opened: ${state.companyProblem} Open it from your profile.` : '',
    ]
      .filter(Boolean)
      .join(' '),
  );
  // The day each paper expires, for the ones the server says need it.
  const [expiries, setExpiries] = useState({});
  const inputs = useRef({});

  const fetchAll = useCallback(async () => {
    try {
      const [application, documents] = await Promise.all([rider.application(), rider.documents()]);
      setLoad({ status: 'ready', application, slots: documents.slots });
    } catch (error) {
      if (isApiError(error) && error.status === 401) {
        clearDriverSession();
        setLoad({ status: 'signed-out' });
        return;
      }
      setLoad({ status: 'failed', message: refusalMessage(error) });
    }
  }, [rider]);

  useEffect(() => {
    void fetchAll();
  }, [fetchAll]);

  if (!isDriverSignedIn() || load.status === 'signed-out') {
    return <Navigate to="/driver/login" replace />;
  }

  const send = async (slot, file) => {
    setProblem('');
    const expiresOn = expiries[slot.type] || '';
    if (slot.requires_expiry && !expiresOn) {
      setProblem(`Tell us when your ${slot.type_label.toLowerCase()} expires, then pick the photo again.`);
      return;
    }
    setSending(slot.type);
    try {
      const body = await compress(file, 1600, 0.8);
      await rider.sendDocument(slot.type, body, slot.requires_expiry ? expiresOn : null);
      await fetchAll();
    } catch (error) {
      setProblem(refusalMessage(error));
    } finally {
      setSending(null);
    }
  };

  const signOut = async () => {
    await rider.signOut();
    clearDriverSession();
    navigate('/driver/login', { replace: true });
  };

  const mayWork = load.status === 'ready' && load.application.may_work === true;

  return (
    <div className="dp-reg-page">
      <header className="dp-reg-nav">
        <h1 className="dp-reg-nav-title">Your application</h1>
      </header>

      <main className="dp-reg-main">
        <div className="dp-reg-card">
          {load.status === 'loading' && (
            <p className="dp-reg-foot" role="status">
              Loading…
            </p>
          )}

          {load.status === 'failed' && (
            <>
              <p className="dp-reg-error" role="alert">
                {load.message}
              </p>
              <button type="button" className="dp-reg-btn-next" onClick={() => void fetchAll()}>
                Try again
              </button>
            </>
          )}

          {load.status === 'ready' && (
            <>
              <div className="dp-reg-deposit" role="status">
                <p className="dp-reg-deposit-text">{mayWork ? 'You are approved. You can start working.' : load.application.refusal}</p>
              </div>

              {problem ? (
                <p className="dp-reg-error" role="alert">
                  {problem}
                </p>
              ) : null}

              <h2 className="dp-reg-docs-title">Your documents</h2>

              {load.slots.map((slot) => {
                const document = slot.document;
                const settled = document?.compliance_state === 'verified';
                // Only what the rider owes is sent from here: one not sent, rejected
                // or expired. One waiting for the office stays as it is.
                const owed = !document || ['rejected', 'expired'].includes(document.compliance_state);
                // The server says which papers are their date (the licence, the insurance).
                const needsDate = slot.requires_expiry === true && owed;

                return (
                  <div key={slot.type}>
                    {needsDate && (
                      <LicenceExpiryField
                        id={`expiry-${slot.type}`}
                        label={`${slot.type_label} expires on`}
                        value={expiries[slot.type] || ''}
                        onChange={(value) => setExpiries((current) => ({ ...current, [slot.type]: value }))}
                      />
                    )}
                    <input
                      ref={(el) => {
                        inputs.current[slot.type] = el;
                      }}
                      type="file"
                      accept="image/*,application/pdf"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        e.target.value = '';
                        if (file) void send(slot, file);
                      }}
                      aria-label={slot.type_label}
                    />
                    <button
                      type="button"
                      className={settled ? 'dp-reg-doc-btn dp-reg-doc-btn--ok' : 'dp-reg-doc-btn'}
                      onClick={() => inputs.current[slot.type]?.click()}
                      disabled={!owed || sending !== null}
                    >
                      {settled ? <CheckIcon /> : <UplIcon />}
                      <span className="dp-reg-doc-btn-label">{slot.type_label}</span>
                      <span className="dp-reg-doc-btn-hint">
                        {sending === slot.type ? 'Sending…' : document ? STATE_WORDS[document.compliance_state] : 'Tap to upload'}
                      </span>
                      {document?.compliance_state === 'rejected' && document.rejection_reason && (
                        <span className="dp-reg-doc-btn-file">{document.rejection_reason}</span>
                      )}
                    </button>
                  </div>
                );
              })}

              {fleetRole === 'owner' ? (
                // An owner runs their company whether or not they ride (InGo ADR 0032, F1).
                <Link to="/driver/fleet" className="dp-reg-btn-next" style={{ display: 'block', textAlign: 'center', textDecoration: 'none', marginBottom: '0.75rem' }}>
                  Run your fleet
                </Link>
              ) : null}

              {mayWork ? (
                <button type="button" className="dp-reg-btn-next" onClick={() => navigate('/driver/home', { replace: true })}>
                  Start working
                </button>
              ) : (
                <button type="button" className="dp-reg-btn-next" onClick={() => void fetchAll()} disabled={sending !== null}>
                  Check again
                </button>
              )}
            </>
          )}

          <p className="dp-reg-foot">
            <button type="button" className="dp-reg-text-btn" onClick={() => void signOut()}>
              Sign out
            </button>
          </p>
          {/* A rider still waiting for approval may delete too (ADR 0038). */}
          <p className="dp-reg-foot">
            <button type="button" className="dp-reg-text-btn" onClick={() => navigate('/driver/delete-account')}>
              Delete my account
            </button>
          </p>
        </div>
      </main>
    </div>
  );
}
